import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getCharacterProfile, resetCharacter, type CharacterProfile } from '@/services/character';
import { ARCHETYPE_V2_INFO, type ArchetypeCode } from '@/types';
import { archetypeDescriptions } from '@/constants/archetypeDescriptions';
import { useTelegramBackButton } from '@/hooks/useTelegramBackButton';

// ============================================
// CHARACTERISTIC METADATA
// ============================================

interface CharMeta {
  id: keyof CharacterProfile['characteristics'];
  name: string;
  emoji: string;
  description: string;
  subskills: string[];
  eventHints: string[];
}

const CHAR_META: CharMeta[] = [
  {
    id: 'body_energy',
    name: 'Энергия тела',
    emoji: '💪',
    description: 'Физический ресурс, выносливость, способность действовать. Влияет на продуктивность и устойчивость к стрессу.',
    subskills: ['Физическая активность', 'Качество сна', 'Питание', 'Связь с телом', 'Управление энергией'],
    eventHints: ['Спорт и движение', 'Йога', 'Дыхательные практики', 'Танцы'],
  },
  {
    id: 'attention',
    name: 'Внимание',
    emoji: '🎯',
    description: 'Способность фокусироваться и замечать важное. Определяет качество решений и глубину восприятия.',
    subskills: ['Фокус', 'Осознанность', 'Медитация', 'Управление отвлечениями', 'Наблюдение'],
    eventHints: ['Медитации', 'Сессии фокуса', 'Осознанность', 'Ретриты тишины'],
  },
  {
    id: 'emotional_regulation',
    name: 'Эмоциональная регуляция',
    emoji: '🌊',
    description: 'Умение управлять своими состояниями. Влияет на отношения и устойчивость в сложных ситуациях.',
    subskills: ['Распознавание эмоций', 'Управление стрессом', 'Самоуспокоение', 'Эмоциональная гибкость'],
    eventHints: ['Терапевтические группы', 'Эмоциональная грамотность', 'Стресс-менеджмент'],
  },
  {
    id: 'communication',
    name: 'Коммуникация',
    emoji: '💬',
    description: 'Навык выражать мысли и слышать других. Определяет качество связей и влияние на людей.',
    subskills: ['Активное слушание', 'Ясное выражение', 'Обратная связь', 'Публичные выступления'],
    eventHints: ['Тренинги общения', 'Публичные выступления', 'Дебаты', 'Сторителлинг'],
  },
  {
    id: 'trust_bonds',
    name: 'Связи и доверие',
    emoji: '🤝',
    description: 'Глубина отношений с близкими. Влияет на поддержку, которую ты получаешь и даёшь.',
    subskills: ['Уязвимость', 'Границы', 'Глубокие разговоры', 'Поддержка других', 'Разрешение конфликтов'],
    eventHints: ['Круги доверия', 'Парная работа', 'Практика уязвимости', 'Групповой шеринг'],
  },
  {
    id: 'agency',
    name: 'Субъектность',
    emoji: '🚀',
    description: 'Ощущение авторства своей жизни. Определяет способность делать выбор и нести ответственность.',
    subskills: ['Принятие решений', 'Инициатива', 'Ответственность', 'Планирование', 'Настойчивость'],
    eventHints: ['Челленджи', 'Лидерство', 'Постановка целей', 'Группы подотчётности'],
  },
  {
    id: 'creativity',
    name: 'Креативность',
    emoji: '✨',
    description: 'Способность создавать новое. Влияет на решение нестандартных задач и самовыражение.',
    subskills: ['Дивергентное мышление', 'Импровизация', 'Самовыражение', 'Эксперименты'],
    eventHints: ['Творческие мастерские', 'Импровизация', 'Арт-терапия', 'Брейнштормы'],
  },
  {
    id: 'meaning',
    name: 'Смысл',
    emoji: '🧭',
    description: 'Понимание зачем ты делаешь то, что делаешь. Определяет мотивацию и удовлетворённость жизнью.',
    subskills: ['Рефлексия', 'Ценности', 'Жизненная миссия', 'Благодарность', 'Принятие'],
    eventHints: ['Круги рефлексии', 'Мастерские ценностей', 'Философские дискуссии', 'Журналинг'],
  },
];

// ============================================
// MAIN COMPONENT
// ============================================

interface CharacterPageProps {
  onClose: () => void;
  onReset?: () => void;
}

export function CharacterPage({ onClose, onReset }: CharacterPageProps) {
  const [profile, setProfile] = useState<CharacterProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedChar, setExpandedChar] = useState<string | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Telegram BackButton
  useTelegramBackButton(onClose);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await getCharacterProfile();
      setProfile(data);
    } catch (error) {
      console.error('Failed to load character profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await resetCharacter();
      setShowResetModal(false);
      if (onReset) {
        onReset();
      }
    } catch (error) {
      console.error('Failed to reset character:', error);
      alert('Не удалось сбросить персонажа');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-950 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 rounded-full border-3 border-primary-700 border-t-primary-400"
        />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-primary-950 flex flex-col items-center justify-center p-6">
        <p className="text-primary-400 mb-4">Не удалось загрузить профиль</p>
        <button onClick={onClose} className="text-primary-300 underline">Назад</button>
      </div>
    );
  }

  // Archetype info from ARCHETYPE_V2_INFO (for emoji, etc)
  const primaryArch = profile.archetypes.primary
    ? ARCHETYPE_V2_INFO[profile.archetypes.primary as ArchetypeCode]
    : null;
  const secondaryArch = profile.archetypes.secondary
    ? ARCHETYPE_V2_INFO[profile.archetypes.secondary as ArchetypeCode]
    : null;

  // Display name: prefer database JOIN value, fallback to ARCHETYPE_V2_INFO
  const primaryDisplayName = profile.archetypeDisplayName || primaryArch?.displayName || null;
  const secondaryDisplayName = profile.secondaryArchetypeDisplayName || secondaryArch?.displayName || null;

  // Archetype code for expanded description
  const archetypeCode = profile.archetypes.primary as ArchetypeCode | null;
  const archetypeDisplayName = primaryDisplayName;

  return (
    <div className="min-h-screen bg-primary-950 safe-area-top safe-area-bottom">
      {/* Header */}
      <header className="flex items-center justify-center p-4 border-b border-primary-800/50">
        <h1 className="text-white font-medium">Твой персонаж</h1>
      </header>

      <div className="overflow-y-auto">
        {/* Basic profile banner */}
        {!profile.batteryBCompleted && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-4 mt-3 p-3 rounded-xl bg-indigo-900/20 border border-indigo-800/30 cursor-pointer"
            onClick={() => {/* navigate to battery B */}}
          >
            <p className="text-xs text-indigo-300 text-center">
              🔮 Базовый профиль &middot; Пройди Диагностику B и C для точной настройки
            </p>
          </motion.div>
        )}

        {/* Avatar + Archetype */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center pt-6 pb-4 px-4"
        >
          {/* Avatar circle */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500/30 to-primary-600/30 border-2 border-primary-600/50 mx-auto flex items-center justify-center mb-3">
            {primaryArch ? (
              <span className="text-4xl">{primaryArch.emoji}</span>
            ) : (
              <span className="text-4xl">🔮</span>
            )}
          </div>

          <h2 className="text-xl font-bold text-white">
            {profile.user.firstName || profile.user.username || 'Путник'}
          </h2>

          {primaryDisplayName && (
            <p className="text-sm text-primary-400 mt-1">
              {primaryDisplayName}
              {secondaryDisplayName && ` / ${secondaryDisplayName}`}
            </p>
          )}

          <p className="text-xs text-primary-500 mt-1">
            Этап {profile.stage.current} из 7 &middot; {profile.stage.activeDays} активных дней
          </p>
        </motion.section>

        {/* Radar Chart */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="px-4 pb-4"
        >
          <RadarChart characteristics={profile.characteristics} />
        </motion.section>

        {/* Archetype Description (expanded) */}
        {archetypeCode && ARCHETYPE_V2_INFO[archetypeCode] && (() => {
          const extDesc = archetypeDescriptions[archetypeCode];
          const v2 = ARCHETYPE_V2_INFO[archetypeCode];
          return (
            <motion.section
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="px-4 pb-6"
            >
              <div className="p-4 rounded-xl bg-primary-800/30 border border-primary-700/30">
                <h3 className="text-sm font-medium text-accent-warm mb-3 flex items-center gap-2">
                  <span>{v2.emoji}</span>
                  Твой архетип: {archetypeDisplayName || v2.displayName}
                </h3>

                {/* Main description (long version from archetypeDescriptions, fallback to short) */}
                <p className="text-sm text-primary-300 leading-relaxed mb-4">
                  {extDesc?.fullDescription || v2.description}
                </p>

                {/* Strengths */}
                <div className="mb-4">
                  <h4 className="text-xs text-primary-500 uppercase tracking-wide mb-2">Сильные стороны</h4>
                  <div className="flex flex-wrap gap-2">
                    {(extDesc?.strengths || v2.strengths).map((s, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-1 rounded-full bg-green-900/20 text-green-400 border border-green-800/30"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Risk/Shadow */}
                <div className="mb-4">
                  <h4 className="text-xs text-primary-500 uppercase tracking-wide mb-2">На что обратить внимание</h4>
                  <p className="text-xs text-primary-400 leading-relaxed">
                    {extDesc?.shadowRisk || v2.riskDescription}
                  </p>
                </div>

                {/* Development advice */}
                <div className="p-3 rounded-lg bg-indigo-900/20 border border-indigo-800/30">
                  <h4 className="text-xs text-indigo-300 font-medium mb-1">Как развиваться</h4>
                  <p className="text-xs text-primary-400 leading-relaxed">
                    {extDesc?.developmentAdvice || `Используй свои сильные стороны как основу. Практикуй осознанность к своим теневым паттернам. Ищи баланс между ${v2.strengths[0]?.toLowerCase() || 'силой'} и уязвимостью.`}
                  </p>
                </div>
              </div>
            </motion.section>
          );
        })()}

        {/* Characteristic Cards */}
        <section className="px-4 pb-6">
          <h3 className="text-xs text-primary-500 uppercase tracking-wide mb-3">8 характеристик</h3>
          <div className="space-y-2">
            {CHAR_META.map((meta, i) => (
              <motion.div
                key={meta.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.04 }}
              >
                <CharacteristicCard
                  meta={meta}
                  value={profile.characteristics[meta.id]}
                  expanded={expandedChar === meta.id}
                  onToggle={() =>
                    setExpandedChar(expandedChar === meta.id ? null : meta.id)
                  }
                />
              </motion.div>
            ))}
          </div>
        </section>

        {/* Reset button */}
        <section className="px-4 pb-8">
          <button
            onClick={() => setShowResetModal(true)}
            className="w-full py-3 rounded-xl border border-red-900/30 text-red-400 text-sm hover:bg-red-900/10 transition-colors"
          >
            Сбросить персонажа
          </button>
        </section>
      </div>

      {/* Reset confirmation modal */}
      <AnimatePresence>
        {showResetModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => !resetting && setShowResetModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-primary-900 rounded-2xl p-6 w-full max-w-sm border border-primary-700/50"
            >
              <div className="text-center mb-4">
                <span className="text-4xl block mb-3">⚠️</span>
                <h2 className="text-lg font-bold text-white mb-2">Удалить персонажа?</h2>
                <p className="text-sm text-primary-400">
                  Удалить персонажа и начать заново? Весь прогресс будет потерян.
                </p>
              </div>

              <div className="space-y-2">
                <button
                  onClick={handleReset}
                  disabled={resetting}
                  className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-medium disabled:opacity-50"
                >
                  {resetting ? 'Удаление...' : 'Удалить'}
                </button>
                <button
                  onClick={() => setShowResetModal(false)}
                  disabled={resetting}
                  className="w-full py-3 rounded-xl border border-primary-700 text-primary-300 disabled:opacity-50"
                >
                  Отмена
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// RADAR CHART (custom SVG)
// ============================================

function RadarChart({ characteristics }: { characteristics: CharacterProfile['characteristics'] }) {
  const cx = 140;
  const cy = 140;
  const maxR = 100;
  const levels = [25, 50, 75, 100];
  const size = 280;

  const axes = CHAR_META.map((m, i) => ({
    id: m.id,
    label: m.emoji,
    shortName: m.name.split(' ')[0],
    angle: (Math.PI * 2 * i) / CHAR_META.length - Math.PI / 2,
    value: characteristics[m.id],
  }));

  const getPoint = (angle: number, r: number) => ({
    x: cx + Math.cos(angle) * r,
    y: cy + Math.sin(angle) * r,
  });

  // Data polygon
  const dataPoints = axes.map(a => {
    const r = (a.value / 100) * maxR;
    return getPoint(a.angle, r);
  });
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

  return (
    <div className="flex justify-center">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} className="max-w-full">
        {/* Grid rings */}
        {levels.map(level => {
          const r = (level / 100) * maxR;
          const pts = axes.map(a => getPoint(a.angle, r));
          const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
          return (
            <path key={level} d={path} fill="none" stroke="rgb(100 116 139 / 0.2)" strokeWidth="1" />
          );
        })}

        {/* Axis lines */}
        {axes.map(a => {
          const end = getPoint(a.angle, maxR);
          return (
            <line
              key={a.id}
              x1={cx} y1={cy}
              x2={end.x} y2={end.y}
              stroke="rgb(100 116 139 / 0.15)"
              strokeWidth="1"
            />
          );
        })}

        {/* Data shape */}
        <path d={dataPath} fill="rgb(99 102 241 / 0.2)" stroke="rgb(129 140 248)" strokeWidth="2" />

        {/* Data points */}
        {dataPoints.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3} fill="rgb(129 140 248)" />
        ))}

        {/* Labels */}
        {axes.map(a => {
          const labelR = maxR + 22;
          const pos = getPoint(a.angle, labelR);
          return (
            <g key={`label-${a.id}`}>
              <text
                x={pos.x}
                y={pos.y - 6}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-primary-400"
                fontSize="12"
              >
                {a.label}
              </text>
              <text
                x={pos.x}
                y={pos.y + 8}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-primary-500"
                fontSize="8"
              >
                {a.value}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ============================================
// CHARACTERISTIC CARD
// ============================================

function CharacteristicCard({
  meta,
  value,
  expanded,
  onToggle,
}: {
  meta: CharMeta;
  value: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  // Color based on value
  const barColor =
    value >= 60
      ? 'bg-green-500'
      : value >= 30
      ? 'bg-primary-400'
      : 'bg-amber-500';

  return (
    <div className="rounded-xl bg-primary-800/30 border border-primary-700/50 overflow-hidden">
      {/* Main row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 text-left"
      >
        <span className="text-xl flex-shrink-0">{meta.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-white truncate">{meta.name}</span>
            <span className="text-sm font-mono text-primary-300 ml-2">{value}</span>
          </div>
          <div className="h-1.5 bg-primary-700/50 rounded-full overflow-hidden">
            <motion.div
              className={`h-full rounded-full ${barColor}`}
              initial={{ width: 0 }}
              animate={{ width: `${value}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          </div>
        </div>
        <span className={`text-primary-500 text-xs transition-transform ${expanded ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {/* Expanded detail */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 border-t border-primary-700/30 pt-2">
              {/* Description */}
              <p className="text-sm text-primary-300 mb-3">{meta.description}</p>

              {/* Subskills */}
              <p className="text-xs text-primary-500 mb-1.5">Поднавыки</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {meta.subskills.map(s => (
                  <span
                    key={s}
                    className="px-2 py-0.5 bg-primary-700/40 rounded text-xs text-primary-300"
                  >
                    {s}
                  </span>
                ))}
              </div>

              {/* Event recommendations */}
              <p className="text-xs text-primary-500 mb-1.5">Что прокачает</p>
              <div className="flex flex-wrap gap-1">
                {meta.eventHints.map(e => (
                  <span
                    key={e}
                    className="px-2 py-0.5 bg-indigo-900/30 border border-indigo-800/30 rounded text-xs text-indigo-300"
                  >
                    {e}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
